import { FC, useCallback, useContext, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import NumberFormat from 'react-number-format';
import { CurrencyIcon, CurrencyEnum, currencyUtils } from '@spectrocoin/sc-currencies';
import Joi from 'joi';
import AccountSelect from '../../../../components/AccountSelect/AccountSelect';
import Button, { ButtonType } from '../../../../components/Button/Button';
import { RemoteError, RemoteStatus } from '../../../../redux/SharedTypes';
import { useAppDispatch } from '../../../../redux/Store';
import {
	getCurrencyData,
	getProjectData,
} from '../../../../redux/PublicFormSlice/PublicFormSelectors';
import OptionalField from '../../../../components/OptionalField/OptionalField';
import Input from '../../../../components/Input/Input';

import styles from './PublicFormBody.module.scss';
import MessageBlock, {
	BlockStyle,
	MessageType,
} from '../../../../components/MessageBlock/MessageBlock';
import errorMessages, {
	getErrorMessageOrDefault,
} from '../../../../helpers/errorMessageHelper/errorMessageHelper';
import TestIds from '../../../../tests/TestIds';
import {
	compareReceiveCurrencies,
	toDecimal,
} from '../../../../helpers/currencyHelper/currencyHelper';
import { toPlainAmount } from '../../../../helpers/currencyAmountHelper';
import { PublicForm } from '../../../../redux/PublicFormSlice/PublicFormSlice';
import useValidation from '../../../../hooks/useValidation';
import PublicFormContext from '../../Context/PublicFormContext';
import axiosInstance from '../../../../helpers/axiosInstance';
import MerchantPreOrder from '../../../../interfaces/MerchantPreOrder';
import { createMerchantPreorderUrl } from '../../../../endpoints';
import PreorderActions from '../../../../redux/PreorderSlice/PreorderActions';
import { DeviceNameType, getDevice } from '../../../../helpers/deviceHelper';

const messages = defineMessages({
	receiveAmount: {
		id: 'payments.receive_amount',
		defaultMessage: 'Receive amount',
	},
	next: {
		id: 'payments.next',
		defaultMessage: 'Next',
	},
	metaDescription: {
		id: 'payments.public_payment_meta',
		defaultMessage: 'Public payment information',
	},
	orderId: {
		id: 'payments.order_id',
		defaultMessage: 'Order ID',
	},
	description: {
		id: 'payments.description',
		defaultMessage: 'Description',
	},
	enterAmount: {
		id: 'payments.enter_amount',
		defaultMessage: 'Enter amount',
	},
	enterOrderReferenceId: {
		id: 'payments.enter_order_reference_id',
		defaultMessage: 'Enter order reference ID',
	},
	enterDescription: {
		id: 'payments.enter_description',
		defaultMessage: 'Here you can add description of this order',
	},
	x_lessOrEqual_y: {
		id: 'inputErrors.x_less_or_equal_y',
		defaultMessage: '"{Field}" length must be less than or equal to {Max} characters long',
	},
});

const SelectedCurrencyInput = () => {
	const { formatMessage } = useIntl();

	const { form, updateForm } = useContext(PublicFormContext);
	const isAppleOS = getDevice() === DeviceNameType.IPHONE;
	const { receiveAmount, receiveCurrencyCode } = form;

	const currencies = useSelector(getCurrencyData)!;
	const allowedCurrencies = useMemo(
		() => currencies.filter(({ currencyCode }) => currencyUtils.tryGetConfig(currencyCode)),
		[currencies]
	);

	const selectedCurrency = useMemo(
		() => allowedCurrencies.find(({ currencyCode }) => currencyCode === receiveCurrencyCode)!,
		[allowedCurrencies, receiveCurrencyCode]
	);

	if (!selectedCurrency) return null;

	const { currencyCode: selectedCurrencyCode, name: selectedCurrencyName } = selectedCurrency;

	return (
		<div
			className={styles.selectedCurrencyInput}
			data-cy={TestIds.PublicFormReceiveAmountSelectedCurrency}
		>
			<CurrencyIcon
				onClick={() => {
					/* there is some black magic voodoo going on here, mobile needs da voodoo */
				}}
				currencyType={receiveCurrencyCode!}
				className={styles.inputCurrencyIcon}
			/>
			<NumberFormat
				onClick={(e: { stopPropagation: () => void }) => {
					e.stopPropagation();
				}}
				className={styles.numberInput}
				decimalSeparator={isAppleOS ? ',' : '.'}
				decimalScale={
					selectedCurrencyCode && currencyUtils.getConfig(selectedCurrencyCode).precision
				}
				allowNegative={false}
				onChange={(e: any) => updateForm({ receiveAmount: e.target.value })}
				inputMode="decimal"
				thousandSeparator={isAppleOS ? '.' : ','}
				value={receiveAmount}
				autoComplete="off"
				autoFocus
				placeholder={formatMessage(messages.enterAmount)}
				data-cy={TestIds.PublicFormReceiveAmountSelectedCurrencyInput}
			/>
			<div
				className={styles.currencyInfo}
				onClick={() => {
					/* there is some black magic voodoo going on here, mobile needs da voodoo */
				}}
			>
				<div className={styles.code}>{selectedCurrencyCode}</div>
				<div className={styles.name}>{selectedCurrencyName}</div>
			</div>
		</div>
	);
};

const SingleCurrencyInput: FC = () => {
	const { formatMessage } = useIntl();

	return (
		<div
			className={styles.singleCurrencyInput}
			data-cy={TestIds.PublicFormReceiveAmountSingleCurrency}
		>
			<label className={styles.label}>{formatMessage(messages.receiveAmount)}</label>
			<div className={styles.container}>
				<div className={styles.selectedValue}>
					<SelectedCurrencyInput />
				</div>
			</div>
		</div>
	);
};

function throwExpression(): never {
	throw Error();
}
export const useFormValidator = (error?: RemoteError) => {
	const { formatMessage } = useIntl();

	const errorCode = error?.errorCode || null;

	return useMemo(
		() =>
			Joi.object<Partial<PublicForm>>({
				orderId: Joi.string()
					.allow('', null)
					.max(255)
					.message(
						formatMessage(messages.x_lessOrEqual_y, {
							Field: formatMessage(messages.orderId),
							Max: 255,
						})
					)
					.custom((v) => (errorCode === 'MA_12' ? throwExpression() : v))
					.message(formatMessage(errorMessages.MA_12)),
				description: Joi.string()
					.allow('', null)
					.max(255)
					.message(
						formatMessage(messages.x_lessOrEqual_y, {
							Field: formatMessage(messages.description),
							Max: 255,
						})
					),
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[errorCode]
	);
};

const PublicFormBody: FC = () => {
	const { id = '' } = useParams<{ id?: string }>();
	const { formatMessage } = useIntl();
	const dispatch = useAppDispatch();
	const currencies = useSelector(getCurrencyData)!;
	const { currencyCode: autoconvertCurrency } = useSelector(getProjectData)!;
	const { action, form, updateForm, setAction } = useContext(PublicFormContext);
	const { description, orderId, receiveAmount, receiveCurrencyCode } = form;
	const { status: createStatus, error: createError } = action;

	const allowedCurrencies = useMemo(
		() =>
			currencies
				.filter(
					({ currencyCode }) =>
						currencyUtils.tryGetConfig(currencyCode) &&
						(!autoconvertCurrency || currencyCode === autoconvertCurrency)
				)
				.sort((a, b) => compareReceiveCurrencies(a.currencyCode, b.currencyCode)),
		[currencies, autoconvertCurrency]
	);

	const selectedCurrency = useMemo(
		() => allowedCurrencies.find(({ currencyCode }) => currencyCode === receiveCurrencyCode),
		[allowedCurrencies, receiveCurrencyCode]
	);

	const selectOptions = useMemo(
		() =>
			allowedCurrencies.map(({ currencyCode, name }) => ({
				value: currencyCode,
				currencyCode,
				currencyName: name,
			})),
		[allowedCurrencies]
	);

	const handleOpenOrderButtonClick = useCallback(() => {
		setAction({ status: RemoteStatus.InProgress });
		const payload = {
			receiveAmount: toPlainAmount(receiveAmount, true),
			receiveCurrencyCode,
			merchantProjectId: id,
			orderId,
			description,
		};
		axiosInstance
			.post<MerchantPreOrder>(`${createMerchantPreorderUrl()}`, payload)
			.then(({ data }) => {
				dispatch(PreorderActions.setPreorder(data));
				setAction({ status: RemoteStatus.Done, data });
			})
			.catch((e) => setAction({ status: RemoteStatus.Error, error: e.response?.data }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [description, id, orderId, receiveAmount, receiveCurrencyCode]);

	const validator = useFormValidator(createError);
	const [isValid, validationResult] = useValidation({ orderId, description }, validator);

	return (
		<div className={styles.body} data-cy={TestIds.PublicForm}>
			{createError && (
				<MessageBlock
					withIcon
					style={BlockStyle.Border}
					type={MessageType.Error}
					message={formatMessage(getErrorMessageOrDefault(createError))}
				/>
			)}
			<div className={styles.currency} data-cy={TestIds.PublicFormReceiveAmount}>
				{allowedCurrencies.length === 1 ? (
					<SingleCurrencyInput />
				) : (
					<AccountSelect
						label={formatMessage(messages.receiveAmount)}
						labelClassName={styles.label}
						value={selectedCurrency?.currencyCode || null}
						options={selectOptions}
						componentOverrides={{
							SingleValue: SelectedCurrencyInput,
						}}
						onChange={(type) =>
							updateForm({ receiveCurrencyCode: type as CurrencyEnum })
						}
					/>
				)}
			</div>
			<OptionalField
				data-cy={TestIds.PublicFormOrderIdField}
				name="orderid"
				id="orderid"
				label={messages.orderId}
				onChange={(value: string | null) => {
					updateForm({ orderId: value });
				}}
			>
				{({ onChange }) => {
					return (
						<Input
							data-cy={TestIds.PublicFormOrderIdInput}
							placeholder={formatMessage(messages.enterOrderReferenceId)}
							value={orderId || ''}
							onChange={(value) => onChange(value)}
							errorMessage={validationResult.orderId}
						/>
					);
				}}
			</OptionalField>
			<OptionalField
				data-cy={TestIds.PublicFormDescriptionField}
				name="description"
				id="description"
				label={messages.description}
				onChange={(value: string | null) => {
					updateForm({ description: value });
				}}
			>
				{({ onChange }) => {
					return (
						<Input
							data-cy={TestIds.PublicFormDescriptionInput}
							placeholder={formatMessage(messages.enterDescription)}
							value={description || ''}
							onChange={(value) => onChange(value)}
							errorMessage={validationResult.description}
						/>
					);
				}}
			</OptionalField>
			<Button
				isFullWidth
				data-cy={TestIds.PublicFormSubmit}
				className={styles.button}
				onClick={handleOpenOrderButtonClick}
				text={formatMessage(messages.next)}
				type={ButtonType.BUTTON}
				isDisabled={
					createStatus === RemoteStatus.InProgress ||
					!receiveAmount ||
					!toDecimal(toPlainAmount(receiveAmount)).greaterThan(0) ||
					!receiveCurrencyCode ||
					!isValid
				}
			/>
		</div>
	);
};

export default PublicFormBody;
